import {
  MAX_ZOOM,
  MIN_ZOOM,
  clamp,
  connectorGeometry,
  createThreadId,
  isTextual,
  nodeAABB,
  screenToBoard,
  selectionBounds,
  selectionRotation,
  vDist,
  visibleRect,
  zoomAt,
  type BoardNode,
  type ConnectorEndpoint,
  type NodeId,
  type Vec,
} from '@kanvas/board-core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { dropFiles, paste } from '../actions/clipboard';
import { duplicateSelection } from '../actions/actions';
import type { BoardDoc } from '../collab/BoardDoc';
import type { Identity } from '../lib/identity';
import { animations } from '../render/animations';
import { BoardRenderer } from '../render/Renderer';
import { hitHandle, type SelectionFrame } from '../render/handles';
import { cancelViewportAnimation } from '../state/camera';
import {
  invalidate,
  resetGestureScratch,
  scratch,
  type AnchorHint,
} from '../state/scratch';
import { useEditor } from '../state/store';
import {
  createBendGesture,
  createBoxGesture,
  createConnectorEndpointGesture,
  createConnectorGesture,
  createCropDrawGesture,
  createCropGesture,
  createCropMoveGesture,
  createDrawGesture,
  createEraseGesture,
  createLaserGesture,
  createMarqueeGesture,
  createMoveGesture,
  createPanGesture,
  createResizeGesture,
  createRotateGesture,
  applyImageCrop,
  beginImageCrop,
  cancelImageCrop,
} from '../interaction/gestures';
import { pickAnchor, pickFrameHeader, pickLeaf, pickNode, slop } from '../interaction/picking';
import { DRAG_THRESHOLD, type Gesture, type GestureContext, type Mods, type PointerInfo } from '../interaction/types';
import { hitCropHandle } from '../render/cropHit';

export interface BoardCanvasProps {
  doc: BoardDoc;
  identity: Identity;
  onRendererReady?: (renderer: BoardRenderer) => void;
  onCreateComment?: (at: Vec, threadId: string) => void;
}

/** Screen-pixel radius for grabbing a connector grip. */
const GRIP_SLOP = 8;

/** How far apart two presses may be and still count as a double-click. */
const DOUBLE_CLICK_SLOP = 6;

/**
 * How often our own cursor position goes out to peers.
 *
 * Awareness has no deltas: every call replaces the whole local state and puts
 * it on the wire, in-progress stroke included. Publishing from the raw
 * pointermove therefore sent the entire stroke so far on every sample, so a
 * long stroke got slower the longer it grew, and it defeated the draft
 * broadcast's own throttle. Peers interpolate between packets anyway, so 20 Hz
 * looks the same to them and costs a fraction of the traffic.
 */
const CURSOR_PUBLISH_MS = 50;

/** CSS cursor for the idle tool — draw/eraser hide the system cursor entirely. */
function cursorForTool(tool: string, space: boolean): string {
  if (space || tool === 'hand') return 'grab';
  if (tool === 'draw' || tool === 'eraser') return 'none';
  if (tool === 'laser') return 'crosshair';
  if (tool === 'select') return 'default';
  return 'crosshair';
}

/**
 * Selection / camera presence. Peers only need approximate viewports for the
 * off-screen hints and follow mode — 10 Hz is enough and keeps awareness quiet
 * during camera tweens. Followers also smooth locally, so sparse packets are fine.
 */
const VIEWPORT_PUBLISH_MS = 100;

/** Which key counts as "primary". Read once: it cannot change mid-session. */
const IS_MAC = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);

export function BoardCanvas({
  doc,
  identity,
  onRendererReady,
  onCreateComment,
}: BoardCanvasProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<BoardRenderer | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const spaceRef = useRef(false);
  /**
   * Cached canvas box, because `getBoundingClientRect` forces layout.
   *
   * Every pointer sample has to be turned into canvas-local coordinates, and a
   * pen delivers a dozen samples per frame. Measuring the element that many
   * times a frame makes the browser flush layout that many times a frame, which
   * on a page with as much chrome as this one costs more than painting the
   * board does. The box is invalidated whenever it can actually move.
   */
  const rectRef = useRef<DOMRect | null>(null);
  /** Active touch points, for two-finger pan and pinch. */
  const touchesRef = useRef(new Map<number, Vec>());
  const pinchRef = useRef<{ distance: number; center: Vec } | null>(null);
  const lastClickRef = useRef<{ at: number; id: NodeId | null; screen: Vec }>({
    at: 0,
    id: null,
    screen: { x: -1e9, y: -1e9 },
  });

  // -------------------------------------------------------------------------
  // Renderer lifecycle
  // -------------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = hostRef.current;
    if (!canvas || !host) return;

    const renderer = new BoardRenderer(canvas, doc);
    rendererRef.current = renderer;
    onRendererReady?.(renderer);

    const applySize = () => {
      rectRef.current = null;
      const rect = host.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      renderer.resize(rect.width, rect.height, dpr);
      useEditor.getState().setViewport((vp) => ({ ...vp, width: rect.width, height: rect.height }));
    };

    // Anything that scrolls an ancestor moves the canvas without resizing it,
    // so the cached box is dropped on scroll as well as on resize.
    const dropRect = () => {
      rectRef.current = null;
    };

    applySize();
    const observer = new ResizeObserver(applySize);
    observer.observe(host);
    window.addEventListener('resize', applySize);
    window.addEventListener('scroll', dropRect, true);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', applySize);
      window.removeEventListener('scroll', dropRect, true);
      renderer.destroy();
      rendererRef.current = null;
    };
  }, [doc, onRendererReady]);

  // -------------------------------------------------------------------------
  // Presence
  // -------------------------------------------------------------------------

  // Identity is also set in BoardDoc's constructor; keep this effect so
  // profile renames while the board is open still propagate.
  useEffect(() => {
    doc.setPresence({
      userId: identity.userId,
      name: identity.name,
      color: identity.color,
      avatar: identity.avatar,
    });
  }, [doc, identity]);

  // Publish selection and camera changes, throttled below pointer rate.
  useEffect(() => {
    let timer = 0;
    let sentAt = 0;
    let pending: {
      selection: string[];
      viewport: ReturnType<typeof visibleRect>;
      tool: string;
    } | null = null;

    const flush = () => {
      timer = 0;
      if (!pending) return;
      sentAt = performance.now();
      doc.setPresence(pending);
      pending = null;
    };

    const unsub = useEditor.subscribe((state) => {
      pending = {
        selection: state.selection,
        viewport: visibleRect(state.viewport),
        tool: state.tool,
      };
      const wait = VIEWPORT_PUBLISH_MS - (performance.now() - sentAt);
      if (wait <= 0) {
        if (timer) {
          clearTimeout(timer);
          timer = 0;
        }
        flush();
      } else if (!timer) {
        timer = window.setTimeout(flush, wait);
      }
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [doc]);

  // Mirror peer laser trails into the render scratch.
  useEffect(() => {
    return doc.subscribePresence(() => {
      let changed = false;
      for (const peer of doc.getPresence()) {
        if (!(peer.laser && peer.laser.length > 0)) continue;
        const prev = scratch.lasers.get(peer.userId);
        // Only refresh when the trail itself moved — cursor packets must not
        // reset the fade timer and keep the rAF loop hot forever.
        if (!prev || prev.points !== peer.laser || prev.color !== peer.color) {
          scratch.lasers.set(peer.userId, {
            points: peer.laser,
            updatedAt: performance.now(),
            color: peer.color,
          });
          changed = true;
        }
      }
      if (changed) invalidate();
    });
  }, [doc]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  const canvasRect = useCallback((): DOMRect => {
    let rect = rectRef.current;
    if (!rect) {
      rect = canvasRef.current!.getBoundingClientRect();
      rectRef.current = rect;
    }
    return rect;
  }, []);

  const pointerFromEvent = useCallback((e: PointerEvent | WheelEvent | MouseEvent): PointerInfo => {
    const rect = canvasRect();
    const screen: Vec = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const vp = useEditor.getState().viewport;
    const pressureSource = e as PointerEvent;
    return {
      screen,
      board: screenToBoard(vp, screen),
      pressure:
        pressureSource.pointerType === 'pen' && pressureSource.pressure > 0
          ? pressureSource.pressure
          : 0.5,
      pointerType: (pressureSource.pointerType as PointerInfo['pointerType']) ?? 'mouse',
    };
  }, [canvasRect]);

  const modsFromEvent = useCallback((e: PointerEvent | MouseEvent | KeyboardEvent): Mods => {
    return {
      shift: e.shiftKey,
      alt: e.altKey,
      primary: IS_MAC ? e.metaKey : e.ctrlKey,
      space: spaceRef.current,
      secondary: 'button' in e ? e.button === 2 : false,
    };
  }, []);

  const contextFor = useCallback(
    (e: PointerEvent | MouseEvent): GestureContext => ({
      doc,
      identity,
      pointer: pointerFromEvent(e),
      mods: modsFromEvent(e),
    }),
    [doc, identity, modsFromEvent, pointerFromEvent],
  );

  const selectionFrame = useCallback((): SelectionFrame | null => {
    const selected = doc.getMany(useEditor.getState().selection);
    if (selected.length === 0) return null;
    // Same rule the renderer uses to draw handles: a lone connector shows
    // endpoint grips instead, and a locked selection shows none. Hit-testing
    // handles that aren't drawn turned clicks on empty canvas into no-op
    // rotate / resize gestures with a resize cursor over nothing.
    if (selected.length === 1 && selected[0]!.type === 'connector') return null;
    if (selected.some((n) => n.locked)) return null;
    return {
      bounds: selectionBounds(selected, doc.lookup),
      rotation: selectionRotation(selected),
    };
  }, [doc]);

  const openCanvasContextMenu = useCallback(
    (clientX: number, clientY: number) => {
      const editor = useEditor.getState();
      const pointer = pointerFromEvent({ clientX, clientY } as MouseEvent);
      const hit = pickNode(doc, pointer.board, {
        vp: editor.viewport,
        enteredGroups: new Set(editor.enteredGroups),
      });
      if (hit && !editor.selection.includes(hit.id)) editor.select([hit.id]);
      editor.openContextMenu({
        x: clientX,
        y: clientY,
        nodeId: hit?.id ?? null,
      });
    },
    [doc, pointerFromEvent],
  );

  // -------------------------------------------------------------------------
  // Gesture selection
  // -------------------------------------------------------------------------

  const decideGesture = useCallback(
    (ctx: GestureContext, e: PointerEvent): Gesture | null => {
      const editor = useEditor.getState();
      const vp = editor.viewport;

      // Space, middle button, right-button drag, or the hand tool always pans.
      if (ctx.mods.space || e.button === 1 || e.button === 2 || editor.tool === 'hand') {
        const originClient = { x: e.clientX, y: e.clientY };
        return createPanGesture(
          ctx,
          e.button === 2
            ? { onIdleUp: () => openCanvasContextMenu(originClient.x, originClient.y) }
            : undefined,
        );
      }

      switch (editor.tool) {
        case 'draw':
          return createDrawGesture(ctx);
        case 'eraser':
          return createEraseGesture(ctx);
        case 'laser':
          return createLaserGesture(ctx);
        case 'shape':
          return createBoxGesture(ctx, 'shape');
        case 'sticky':
          return createBoxGesture(ctx, 'sticky');
        case 'text': {
          // Clicking existing text with the text tool opens it for editing at
          // that spot instead of stacking a new empty text box on top of it.
          const under = pickNode(doc, ctx.pointer.board, {
            vp,
            enteredGroups: new Set(editor.enteredGroups),
          });
          if (
            under &&
            isTextual(under) &&
            under.type !== 'connector' &&
            !under.locked &&
            e.button === 0 &&
            !ctx.mods.shift
          ) {
            editor.select([under.id]);
            editor.setEditing(under.id, ctx.pointer.board);
            invalidate();
            return null;
          }
          return createBoxGesture(ctx, 'text');
        }
        case 'frame':
          return createBoxGesture(ctx, 'frame');
        case 'notebook':
          return createBoxGesture(ctx, 'notebook');
        case 'comment': {
          const threadId = createThreadId();
          onCreateComment?.(ctx.pointer.board, threadId);
          if (!editor.toolLocked) editor.setTool('select');
          return null;
        }
        case 'connector': {
          const anchor = pickAnchor(doc, ctx.pointer.board, vp);
          if (anchor) {
            return createConnectorGesture(
              ctx,
              {
                kind: 'node',
                nodeId: anchor.nodeId,
                anchor: { kind: 'fixed', nx: anchor.nx, ny: anchor.ny },
              },
              anchor.point,
            );
          }
          const under = pickNode(doc, ctx.pointer.board, { vp, frameEdgeOnly: false });
          const start: ConnectorEndpoint = under
            ? { kind: 'node', nodeId: under.id, anchor: { kind: 'auto' } }
            : { kind: 'point', x: ctx.pointer.board.x, y: ctx.pointer.board.y };
          return createConnectorGesture(ctx, start, ctx.pointer.board);
        }
      }

      // ---- crop mode ------------------------------------------------------
      if (editor.croppingId) {
        const image = doc.get(editor.croppingId);
        if (image?.type === 'image') {
          const hit = hitCropHandle(image, vp, ctx.pointer.screen);
          if (hit && hit !== 'move' && hit !== 'draw') {
            return createCropGesture(ctx, hit, image);
          }
          // Alt+drag moves the rectangular window; plain drag draws a freehand mask.
          if (hit === 'draw' || hit === 'move') {
            if (ctx.mods.alt) return createCropMoveGesture(ctx, image);
            return createCropDrawGesture(ctx, image);
          }
          // Click outside the image commits the crop.
          applyImageCrop(ctx);
          return null;
        }
        cancelImageCrop();
      }

      // ---- select tool ----------------------------------------------------
      const frame = selectionFrame();

      if (frame && editor.editingId === null) {
        const handle = hitHandle(frame, vp, ctx.pointer.screen, {
          resize: !editor.selection.every((id) => doc.get(id)?.type === 'notebook'),
        });
        if (handle) {
          const ids = editor.selection;
          return handle.id === 'rotate'
            ? createRotateGesture(ctx, ids, frame)
            : createResizeGesture(ctx, ids, handle.id, frame);
        }
      }

      // Connector grips take precedence over anything underneath them.
      if (editor.selection.length === 1) {
        const only = doc.get(editor.selection[0]!);
        if (only?.type === 'connector') {
          const geo = connectorGeometry(only, doc.lookup);
          const toScreen = (p: Vec) => ({
            x: (p.x - vp.x) * vp.zoom,
            y: (p.y - vp.y) * vp.zoom,
          });
          if (vDist(toScreen(geo.start), ctx.pointer.screen) <= GRIP_SLOP) {
            return createConnectorEndpointGesture(ctx, only.id, 'start');
          }
          if (vDist(toScreen(geo.end), ctx.pointer.screen) <= GRIP_SLOP) {
            return createConnectorEndpointGesture(ctx, only.id, 'end');
          }
          if (vDist(toScreen(geo.mid), ctx.pointer.screen) <= GRIP_SLOP) {
            return createBendGesture(ctx, only.id);
          }
        }
      }

      const entered = new Set(editor.enteredGroups);
      const header = pickFrameHeader(doc, ctx.pointer.board, vp);
      const hit = header ?? pickNode(doc, ctx.pointer.board, { vp, enteredGroups: entered });

      if (!hit) {
        if (!ctx.mods.shift) editor.clearSelection();
        if (entered.size > 0) editor.exitGroups();
        return createMarqueeGesture(ctx, ctx.mods.shift);
      }

      if (ctx.mods.shift) {
        editor.toggleInSelection(hit.id);
        return null;
      }

      if (!editor.selection.includes(hit.id)) editor.select([hit.id]);
      if (hit.locked) return null;

      // Alt-drag duplicates, matching every other canvas tool. The copy is
      // made on the first real drag, not on press: an Alt+click that never
      // moved used to leave an invisible duplicate stacked on the original.
      if (ctx.mods.alt) return createAltDuplicateGesture(ctx, { doc, identity });

      return createMoveGesture(ctx, useEditor.getState().selection);
    },
    [doc, identity, onCreateComment, openCanvasContextMenu, selectionFrame],
  );

  // -------------------------------------------------------------------------
  // Pointer events
  // -------------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let cursorPending: Vec | null = null;
    let cursorTimer = 0;
    let cursorSentAt = 0;
    /** Pen hover floods pointermove; hit-testing once per frame is enough. */
    let hoverRaf = 0;
    let hoverPending: GestureContext | null = null;

    const flushCursor = () => {
      cursorTimer = 0;
      if (!cursorPending) return;
      cursorSentAt = performance.now();
      doc.setPresence({ cursor: cursorPending });
      cursorPending = null;
    };

    /** Queues our cursor for peers; `null` leaves immediately, as it is final. */
    const publishCursor = (at: Vec | null) => {
      if (at === null) {
        cursorPending = null;
        if (cursorTimer) {
          clearTimeout(cursorTimer);
          cursorTimer = 0;
        }
        doc.setPresence({ cursor: null });
        return;
      }
      // Quantize so pen micro-jitter does not spam awareness with no-op floats.
      cursorPending = {
        x: Math.round(at.x * 10) / 10,
        y: Math.round(at.y * 10) / 10,
      };
      const wait = CURSOR_PUBLISH_MS - (performance.now() - cursorSentAt);
      if (wait <= 0) flushCursor();
      else if (!cursorTimer) cursorTimer = window.setTimeout(flushCursor, wait);
    };

    const scheduleHover = (ctx: GestureContext) => {
      hoverPending = ctx;
      if (hoverRaf) return;
      hoverRaf = requestAnimationFrame(() => {
        hoverRaf = 0;
        const pending = hoverPending;
        hoverPending = null;
        if (pending && !gestureRef.current) updateHover(pending);
      });
    };

    const onPointerDown = (e: PointerEvent) => {
      const editor = useEditor.getState();
      editor.closeContextMenu();
      // A press is the one moment the canvas is guaranteed to be still, and it
      // is the last chance to notice that a panel opening moved it.
      rectRef.current = null;
      // Any direct input wins over an in-flight camera move; fighting a tween
      // for control of the viewport is the worst kind of input lag.
      cancelViewportAnimation();

      if (e.pointerType === 'touch') {
        touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touchesRef.current.size === 2) {
          // Second finger down: abandon whatever single-touch gesture started.
          gestureRef.current?.cancel(contextFor(e));
          gestureRef.current = null;
          const [a, b] = [...touchesRef.current.values()];
          pinchRef.current = {
            distance: vDist(a!, b!),
            center: { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 },
          };
          return;
        }
      }

      // One gesture, one pointer: a palm landing while the pen draws must not
      // replace (and silently lose) the stroke in progress.
      if (gestureRef.current && pointerIdRef.current !== null && pointerIdRef.current !== e.pointerId) {
        return;
      }

      canvas.setPointerCapture(e.pointerId);
      pointerIdRef.current = e.pointerId;

      const ctx = contextFor(e);
      const now = performance.now();
      const hit = pickNode(doc, ctx.pointer.board, {
        vp: editor.viewport,
        enteredGroups: new Set(editor.enteredGroups),
      });

      // Double-click detection lives here rather than on dblclick so it shares
      // the same hit-test as the press that precedes it. Distance matters as
      // much as time: a quick click followed by a drag somewhere else is two
      // separate gestures, not a double-click. Middle and right buttons pan
      // (or open the menu) and must not steal the next left-click's pairing.
      const isPrimary = e.button === 0;
      const isDouble =
        isPrimary &&
        now - lastClickRef.current.at < 320 &&
        vDist(lastClickRef.current.screen, ctx.pointer.screen) <= DOUBLE_CLICK_SLOP &&
        lastClickRef.current.id === (hit?.id ?? null) &&
        editor.tool === 'select';
      if (isPrimary) {
        lastClickRef.current = { at: now, id: hit?.id ?? null, screen: ctx.pointer.screen };
      }

      if (isDouble) {
        handleDoubleClick(ctx, hit);
        return;
      }

      gestureRef.current = decideGesture(ctx, e);
      // An object under an active gesture must track the pointer exactly, so
      // any entrance tween still running on it is dropped.
      if (gestureRef.current) animations.cancel(useEditor.getState().selection);
      if (gestureRef.current?.cursor) {
        scratch.cursor = gestureRef.current.cursor;
        canvas.style.cursor = gestureRef.current.cursor;
      }
      invalidate();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch' && touchesRef.current.has(e.pointerId)) {
        touchesRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touchesRef.current.size === 2 && pinchRef.current) {
          const [a, b] = [...touchesRef.current.values()];
          const distance = vDist(a!, b!);
          const center = { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 };
          const rect = canvasRect();
          const localCenter = { x: center.x - rect.left, y: center.y - rect.top };

          const scale = distance / (pinchRef.current.distance || distance);
          const dx = center.x - pinchRef.current.center.x;
          const dy = center.y - pinchRef.current.center.y;

          // Like wheel and pan: taking the camera by hand stops following.
          if (useEditor.getState().followingUserId) {
            useEditor.getState().setUi({ followingUserId: null });
          }
          useEditor.getState().setViewport((vp) => {
            const panned = { ...vp, x: vp.x - dx / vp.zoom, y: vp.y - dy / vp.zoom };
            return zoomAt(panned, panned.zoom * scale, localCenter);
          });
          pinchRef.current = { distance, center };
          return;
        }
      }

      if (gestureRef.current && pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) {
        return;
      }
      // The release was swallowed (alt-tab mid-drag, a dialog stealing the
      // mouse-up): finish the gesture instead of dragging with no button held.
      if (gestureRef.current && e.pointerType === 'mouse' && e.buttons === 0) {
        endGesture(e, false);
        return;
      }

      const gesture = gestureRef.current;
      let ctx: GestureContext;

      if (gesture) {
        // The browser fires one pointermove per frame and hands over the
        // samples it swallowed in between. Ink needs them: a pen reports several
        // times faster than the display refreshes, and a stroke rebuilt from one
        // sample per frame is visibly faceted wherever the hand moved quickly.
        const samples = gesture.coalesce ? coalesced(e) : null;
        if (samples && samples.length > 1) {
          let last!: GestureContext;
          for (const sample of samples) {
            last = contextFor(sample);
            gesture.move(last);
          }
          ctx = last;
        } else {
          ctx = contextFor(e);
          gesture.move(ctx);
        }
      } else {
        ctx = contextFor(e);
        scheduleHover(ctx);
      }

      // Publish the cursor for peers. Board coordinates, so peers see the same
      // point regardless of their own camera.
      publishCursor(ctx.pointer.board);
    };

    const endGesture = (e: PointerEvent, cancelled: boolean) => {
      if (e.pointerType === 'touch') {
        touchesRef.current.delete(e.pointerId);
        if (touchesRef.current.size < 2) pinchRef.current = null;
      }
      // A second pointer lifting (palm, extra finger) doesn't end the gesture.
      if (pointerIdRef.current !== null && pointerIdRef.current !== e.pointerId) return;
      if (pointerIdRef.current === e.pointerId) {
        if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
        pointerIdRef.current = null;
      }
      const gesture = gestureRef.current;
      gestureRef.current = null;
      if (!gesture) return;
      const ctx = contextFor(e);
      if (cancelled) gesture.cancel(ctx);
      else gesture.up(ctx);

      // Keep the tool's cursor after the stroke — resetting to the system arrow
      // made the brush tip vanish for a frame after every mark.
      const editor = useEditor.getState();
      const nextCursor = cursorForTool(editor.tool, spaceRef.current);
      scratch.cursor = nextCursor;
      if (canvas.style.cursor !== nextCursor) canvas.style.cursor = nextCursor;
      if (editor.tool === 'draw') {
        scratch.brushCursorPoint = ctx.pointer.board;
        scratch.eraserPoint = null;
      } else if (editor.tool === 'eraser') {
        scratch.eraserPoint = ctx.pointer.board;
        scratch.eraserRadius = 14 / editor.viewport.zoom;
        scratch.brushCursorPoint = null;
      }
      invalidate();
    };

    const onPointerUp = (e: PointerEvent) => endGesture(e, false);
    const onPointerCancel = (e: PointerEvent) => endGesture(e, true);

    const onPointerLeave = () => {
      if (!gestureRef.current) {
        scratch.hoverId = null;
        scratch.anchorHint = null;
        // Otherwise the brush / eraser ring stays painted at the canvas edge
        // while the pointer is over a panel.
        scratch.brushCursorPoint = null;
        scratch.eraserPoint = null;
        publishCursor(null);
        invalidate();
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('pointerleave', onPointerLeave);

    return () => {
      if (cursorTimer) clearTimeout(cursorTimer);
      if (hoverRaf) cancelAnimationFrame(hoverRaf);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      canvas.removeEventListener('pointerleave', onPointerLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, identity, canvasRect, contextFor, decideGesture]);

  // -------------------------------------------------------------------------
  // Hover
  // -------------------------------------------------------------------------

  const updateHover = useCallback(
    (ctx: GestureContext) => {
      const editor = useEditor.getState();
      const vp = editor.viewport;
      let cursor = cursorForTool(editor.tool, ctx.mods.space);

      const hit =
        editor.tool === 'select' || editor.tool === 'connector'
          ? pickNode(doc, ctx.pointer.board, {
              vp,
              enteredGroups: new Set(editor.enteredGroups),
            })
          : null;

      // Hover only ever changes the overlay, so a frame is worth scheduling
      // only when one of the things the overlay draws actually moved. Without
      // this the whole board repainted on every sample of a pointer merely
      // crossing it — and a pen in range reports continuously without ever
      // touching the tablet, so simply holding one over the desk was enough to
      // keep the renderer at full tilt.
      const hoverId = hit?.id ?? null;
      let changed = scratch.hoverId !== hoverId;
      scratch.hoverId = hoverId;

      const anchorHint =
        editor.tool === 'connector' ? pickAnchor(doc, ctx.pointer.board, vp) : null;
      if (!sameAnchor(scratch.anchorHint, anchorHint)) changed = true;
      scratch.anchorHint = anchorHint;

      if (editor.tool === 'select') {
        const frame = selectionFrame();
        const handle = frame
          ? hitHandle(frame, vp, ctx.pointer.screen, {
              resize: !editor.selection.every((id) => doc.get(id)?.type === 'notebook'),
            })
          : null;
        if (handle) cursor = handle.cursor;
        else if (hit && editor.selection.includes(hit.id)) cursor = 'move';
        else if (hit) cursor = 'pointer';
      }
      if (editor.tool === 'eraser') {
        // The eraser ring follows the pointer exactly, so this one always draws.
        scratch.eraserPoint = ctx.pointer.board;
        scratch.eraserRadius = 14 / vp.zoom;
        scratch.brushCursorPoint = null;
        changed = true;
      } else if (scratch.eraserPoint) {
        scratch.eraserPoint = null;
        changed = true;
      }
      if (editor.tool === 'draw') {
        scratch.brushCursorPoint = ctx.pointer.board;
        changed = true;
      } else if (scratch.brushCursorPoint) {
        scratch.brushCursorPoint = null;
        changed = true;
      }

      scratch.cursor = cursor;
      const canvas = canvasRef.current;
      if (canvas && canvas.style.cursor !== cursor) canvas.style.cursor = cursor;
      if (changed) invalidate();
    },
    [doc, selectionFrame],
  );

  // -------------------------------------------------------------------------
  // Double click
  // -------------------------------------------------------------------------

  const handleDoubleClick = useCallback(
    (ctx: GestureContext, hit: BoardNode | null) => {
      const editor = useEditor.getState();

      if (!hit) {
        // Empty canvas: a double click drops a sticky, the fastest way to
        // capture a thought during a workshop.
        const gesture = createBoxGesture(ctx, 'sticky');
        gesture.up(ctx);
        return;
      }

      if (hit.type === 'group') {
        // Step inside the group and select the actual child under the pointer.
        editor.enterGroup(hit.id);
        const leaf = pickLeaf(doc, ctx.pointer.board, { vp: editor.viewport });
        if (leaf) editor.select([leaf.id]);
        invalidate();
        return;
      }

      if (hit.type === 'notebook') {
        editor.select([hit.id]);
        editor.setEnteredNotebook(hit.id);
        invalidate();
        return;
      }

      if (hit.type === 'image') {
        editor.select([hit.id]);
        beginImageCrop(hit.id);
        invalidate();
        return;
      }

      if (isTextual(hit)) {
        editor.select([hit.id]);
        // Caret where the double-click landed, so you continue writing there.
        editor.setEditing(hit.id, ctx.pointer.board);
        invalidate();
        return;
      }

      if (hit.type === 'frame') {
        // Double-clicking a frame zooms to it, which is how people navigate
        // between frames while presenting.
        const bounds = nodeAABB(hit, doc.lookup);
        editor.setViewport((vp) => {
          const zoom = clamp(
            Math.min((vp.width - 96) / bounds.w, (vp.height - 96) / bounds.h),
            MIN_ZOOM,
            MAX_ZOOM,
          );
          return {
            ...vp,
            zoom,
            x: bounds.x + bounds.w / 2 - vp.width / 2 / zoom,
            y: bounds.y + bounds.h / 2 - vp.height / 2 / zoom,
          };
        });
      }
    },
    [doc],
  );

  // -------------------------------------------------------------------------
  // Wheel
  // -------------------------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      cancelViewportAnimation();
      // Taking the camera by hand means you are no longer following anyone.
      if (useEditor.getState().followingUserId) {
        useEditor.getState().setUi({ followingUserId: null });
      }
      const rect = canvasRect();
      const anchor: Vec = { x: e.clientX - rect.left, y: e.clientY - rect.top };

      // Ctrl/Cmd + wheel, and trackpad pinch (which arrives as ctrlKey), zoom.
      // A plain wheel scrolls, which is what a large board needs by default.
      const scale = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect.height : 1;
      if (e.ctrlKey || e.metaKey) {
        // Normalise line-mode deltas (Firefox) and cap each step: a mouse
        // notch (~100px) used to jump the zoom 2.7x in one frame, while
        // trackpad pinches send small deltas and are unaffected by the cap.
        const step = Math.max(-0.25, Math.min(0.25, -e.deltaY * scale * 0.01));
        const factor = Math.exp(step);
        useEditor.getState().setViewport((vp) => zoomAt(vp, vp.zoom * factor, anchor));
        return;
      }

      const dx = e.deltaX * scale;
      const dy = e.deltaY * scale;
      useEditor.getState().setViewport((vp) => ({
        ...vp,
        x: vp.x + dx / vp.zoom,
        y: vp.y + dy / vp.zoom,
      }));
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [canvasRect]);

  // -------------------------------------------------------------------------
  // Space-to-pan, context menu, file drop
  // -------------------------------------------------------------------------

  useEffect(() => {
    /**
     * Releases held Space.
     *
     * Getting this wrong is unusually damaging: while Space is considered held,
     * *every* drag pans, so the board looks like it has stopped responding to
     * every tool at once and stays that way until reload. And a keyup is easy
     * to miss — switching windows with the key down, or the OS claiming the
     * keystroke, both swallow it. So the release is driven from every event
     * that means "this window no longer owns the keyboard", not from keyup
     * alone, and Escape is a manual way out.
     */
    const releaseSpace = () => {
      if (!spaceRef.current) return;
      spaceRef.current = false;
      const canvas = canvasRef.current;
      if (canvas) canvas.style.cursor = scratch.cursor;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !isTypingTarget(e.target)) {
        spaceRef.current = true;
        const canvas = canvasRef.current;
        if (canvas) canvas.style.cursor = 'grab';
        e.preventDefault();
      }
      if (e.key === 'Escape') {
        releaseSpace();
        if (gestureRef.current) {
          gestureRef.current.cancel({
            doc,
            identity,
            pointer: {
              board: { x: 0, y: 0 },
              screen: { x: 0, y: 0 },
              pressure: 0.5,
              pointerType: 'mouse',
            },
            mods: { shift: false, alt: false, primary: false, space: false, secondary: false },
          });
          gestureRef.current = null;
          resetGestureScratch();
          invalidate();
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') releaseSpace();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') releaseSpace();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', releaseSpace);
    window.addEventListener('pagehide', releaseSpace);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', releaseSpace);
      window.removeEventListener('pagehide', releaseSpace);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [doc, identity]);

  const onContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // Mouse right-click is owned by the pan gesture: a drag moves the camera,
      // a click without a drag opens the menu on pointerup. Keyboard
      // context-menu keys still arrive here (button is not 2).
      if (e.button === 2) return;
      openCanvasContextMenu(e.clientX, e.clientY);
    },
    [openCanvasContextMenu],
  );

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const pointer = pointerFromEvent(e.nativeEvent as unknown as MouseEvent);
      const files = [...e.dataTransfer.files];
      if (files.length > 0) {
        await dropFiles({ doc, identity }, files, pointer.board);
        return;
      }
      const text = e.dataTransfer.getData('text/plain');
      if (text) {
        // Reuse the paste pipeline so a dragged URL behaves like a pasted one.
        await paste({ doc, identity }, pointer.board, {
          clipboardData: e.dataTransfer,
        } as unknown as ClipboardEvent);
      }
    },
    [doc, identity, pointerFromEvent],
  );

  const style = useMemo(() => ({ cursor: scratch.cursor }), []);

  return (
    <div
      ref={hostRef}
      className="board-canvas-host"
      onContextMenu={onContextMenu}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      <canvas ref={canvasRef} className="board-canvas" style={style} />
    </div>
  );
}

/**
 * Every pointer sample the browser folded into one move event.
 *
 * Returns `null` where the API is missing, so the caller falls back to the
 * event itself rather than to an empty stroke.
 */
function sameAnchor(a: AnchorHint | null, b: AnchorHint | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.nodeId === b.nodeId && a.nx === b.nx && a.ny === b.ny;
}

function coalesced(e: PointerEvent): PointerEvent[] | null {
  if (typeof e.getCoalescedEvents !== 'function') return null;
  const samples = e.getCoalescedEvents();
  return samples.length > 0 ? samples : null;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/**
 * Alt-drag: duplicate the selection once the pointer passes the drag
 * threshold, then move the copies. Cancelling the drag removes the copies
 * and gives the originals their selection back.
 */
function createAltDuplicateGesture(
  press: GestureContext,
  action: { doc: BoardDoc; identity: Identity },
): Gesture {
  let inner: Gesture | null = null;
  let originals: NodeId[] = [];
  return {
    cursor: 'copy',
    move(next) {
      if (!inner) {
        if (vDist(next.pointer.screen, press.pointer.screen) < DRAG_THRESHOLD) return;
        originals = [...useEditor.getState().selection];
        duplicateSelection(action, { x: 0, y: 0 });
        inner = createMoveGesture(press, useEditor.getState().selection);
      }
      inner.move(next);
    },
    up(next) {
      inner?.up(next);
    },
    cancel(next) {
      if (!inner) return;
      inner.cancel(next);
      action.doc.remove(useEditor.getState().selection);
      useEditor.getState().select(originals);
    },
  };
}
